import type { PropertyDetailsFragment } from '__generated__/athena/graphql';
import { Badge, ListingHistoryState, ListingState } from '__generated__/athena/graphql';

import { isBuyDirectEligible } from './buyDirectHelpers';

/**
 * Returns the sold price (in dollars) for a recently-sold home by reading the
 * SOLD entry in `listingHistory`. Returns null when there is no SOLD entry or
 * no sold price was recorded (e.g. an off-market sale without a price).
 */
export const getSoldPrice = (property: PropertyDetailsFragment): number | null => {
  const soldEntry = property.listingHistory?.find(
    (entry) => entry.state === ListingHistoryState.SOLD,
  );
  const soldPriceCents = soldEntry?.soldPriceCents;
  if (!soldPriceCents) return null;
  return soldPriceCents / 100;
};

const isRecentlySold = (property: PropertyDetailsFragment): boolean =>
  property.listingState === ListingState.RECENTLY_SOLD ||
  property.displayBadge === Badge.RECENTLY_SOLD;

/**
 * Which price a surface is quoting. `listed` is what the home is listed at;
 * `purchase` is what a buyer would pay, which for a BuyDirect-eligible home is
 * the discounted BuyDirect price.
 *
 * The two differ by the BuyDirect discount, so a surface quoting `purchase`
 * alongside the CTA's "$X below list" badge contradicts the savings that badge
 * computes against the list price.
 */
export type PriceQuote = 'listed' | 'purchase';

/**
 * Returns the price (in dollars) to surface for a property, per the caller's
 * `quote`. Recently-sold homes show their sold price regardless of `quote`,
 * falling back to the list price when no sold price was recorded. Active
 * BuyDirect-eligible homes show the BuyDirect price only under `purchase`,
 * falling back to the list price when no BuyDirect price was recorded.
 * Everything else shows the list price. Returns null when no price is available.
 *
 * `quote` is required so a surface cannot inherit BuyDirect substitution it did
 * not ask for.
 */
export const getDisplayPrice = (
  property: PropertyDetailsFragment,
  quote: PriceQuote,
): number | null => {
  if (isRecentlySold(property)) {
    return getSoldPrice(property) ?? property.listPrice ?? null;
  }
  if (
    quote === 'purchase' &&
    property.displayBadge === Badge.ON_THE_MARKET &&
    isBuyDirectEligible(property)
  ) {
    return property.buyDirectPrice ?? property.listPrice ?? null;
  }
  return property.listPrice ?? null;
};
